import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Shield, User, Trash2, Camera, UserPlus, Upload, FileSpreadsheet, Download } from "lucide-react";
import { toast } from "sonner";
import Avatar, { invalidateAvatar } from "@/components/Avatar";
import MonthDayPicker from "@/components/MonthDayPicker";

export default function Players() {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    jersey_number: user?.jersey_number || "",
    position: user?.position || "",
  });

  const canManage = user?.role === "coach" || user?.role === "admin";
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", jersey_number: "", position: "", email: "", birthday: "" });
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/players");
      setPlayers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setForm({
      name: user?.name || "",
      jersey_number: user?.jersey_number || "",
      position: user?.position || "",
    });
  }, [user]);

  const saveProfile = async (e) => {    e.preventDefault();
    try {
      await api.put("/auth/me", {
        name: form.name,
        jersey_number: form.jersey_number ? Number(form.jersey_number) : null,
        position: form.position || null,
      });
      toast.success("Profil mis à jour");
      setEditing(false);
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const addPlayer = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const payload = {
        name: addForm.name.trim(),
        jersey_number: addForm.jersey_number ? Number(addForm.jersey_number) : null,
        position: addForm.position || null,
        email: addForm.email.trim() || null,
        birthday: addForm.birthday || null,
      };
      await api.post("/players", payload);
      toast.success("Joueur ajouté");
      setShowAdd(false);
      setAddForm({ name: "", jersey_number: "", position: "", email: "", birthday: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setAdding(false);
    }
  };

  const avatarInputRef = useRef(null);

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Photo trop volumineuse (max 3 Mo)");
      return;
    }
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API}/players/${user.id}/avatar`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) throw new Error("upload");
      invalidateAvatar(user.id);
      toast.success("Photo de profil enregistrée");
      load();
    } catch (err) {
      toast.error("Échec de l'upload");
    }
  };

  const coaches = players.filter((p) => p.role === "coach" || p.role === "admin");
  const regular = players.filter((p) => p.role === "player");

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-slate-500 mb-2">
            Effectif du club
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
            Joueurs
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <button
            data-testid="edit-profile-button"
            onClick={() => setEditing(!editing)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold uppercase tracking-wide whitespace-nowrap"
          >
            {editing ? "Fermer" : "Éditer mon profil"}
          </button>
          {canManage && (
            <button
              data-testid="add-player-button"
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] text-sm font-bold uppercase tracking-wide whitespace-nowrap"
            >
              <UserPlus className="w-4 h-4" /> Ajouter un joueur
            </button>
          )}
          {canManage && <ImportCSVButton onDone={load} />}
        </div>
      </div>

      {showAdd && (
        <form
          onSubmit={addPlayer}
          data-testid="add-player-form"
          className="rounded-2xl bg-slate-900 border border-[#E5B85C]/30 p-5 space-y-4 max-w-lg"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] font-mono-stat uppercase tracking-widest text-[#E5B85C]">
                Nouvelle fiche
              </div>
              <div className="font-display font-bold text-lg uppercase">Ajouter un joueur</div>
            </div>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="text-slate-400 hover:text-white text-xs uppercase tracking-widest"
            >
              Annuler
            </button>
          </div>
          <p className="text-xs text-slate-400">
            Le joueur peut plus tard s'inscrire avec le même email pour reprendre sa fiche
            et voir ses stats.
          </p>
          <Field label="Nom *">
            <input
              data-testid="add-player-name"
              type="text"
              required
              value={addForm.name}
              onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
              className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white"
              placeholder="Prénom Nom"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="N° maillot">
              <input
                data-testid="add-player-jersey"
                type="number"
                min="0"
                max="99"
                value={addForm.jersey_number}
                onChange={(e) => setAddForm({ ...addForm, jersey_number: e.target.value })}
                className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white"
                placeholder="10"
              />
            </Field>
            <Field label="Poste">
              <select
                data-testid="add-player-position"
                value={addForm.position}
                onChange={(e) => setAddForm({ ...addForm, position: e.target.value })}
                className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white"
              >
                <option value="">—</option>
                {["Gardien", "Défenseur", "Milieu", "Attaquant"].map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Email (facultatif — pour claim futur)">
            <input
              data-testid="add-player-email"
              type="email"
              value={addForm.email}
              onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
              className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white"
              placeholder="joueur@exemple.com"
            />
          </Field>
          <Field label="Date de naissance — jour et mois (facultatif)">
            <MonthDayPicker
              testIdPrefix="add-player-birthday"
              value={addForm.birthday || ""}
              onChange={(v) => setAddForm({ ...addForm, birthday: v })}
            />
          </Field>
          <button
            data-testid="submit-add-player"
            type="submit"
            disabled={adding}
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg disabled:opacity-50"
          >
            <UserPlus className="w-4 h-4" />
            {adding ? "Ajout..." : "Créer la fiche"}
          </button>
        </form>
      )}

      {editing && (
        <form
          onSubmit={saveProfile}
          className="rounded-2xl bg-slate-900 border border-white/10 p-5 space-y-4 max-w-lg"
        >
          <div className="flex items-center gap-4">
            <Avatar player={user} size={64} />
            <div>
              <input
                ref={avatarInputRef}
                data-testid="avatar-file-input"
                type="file"
                accept="image/*"
                onChange={uploadAvatar}
                className="hidden"
              />
              <button
                type="button"
                data-testid="upload-avatar-button"
                onClick={() => avatarInputRef.current?.click()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold uppercase tracking-wide"
              >
                <Camera className="w-3.5 h-3.5" /> Photo de profil
              </button>
              <div className="text-[10px] text-slate-500 font-mono-stat mt-1">
                jpg / png / webp, max 3 Mo
              </div>
            </div>
          </div>
          <Field label="Nom">
            <input
              data-testid="profile-name-input"
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="N° maillot">
              <input
                data-testid="profile-jersey-input"
                type="number"
                min="1"
                max="99"
                value={form.jersey_number}
                onChange={(e) => setForm({ ...form, jersey_number: e.target.value })}
                className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white"
              />
            </Field>
            <Field label="Poste">
              <input
                data-testid="profile-position-input"
                type="text"
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white"
                placeholder="Attaquant"
              />
            </Field>
          </div>
          <button
            data-testid="save-profile-button"
            type="submit"
            className="px-5 py-3 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg"
          >
            Enregistrer
          </button>
        </form>
      )}

      {loading ? (
        <div className="text-slate-400 text-sm font-mono-stat uppercase">Chargement...</div>
      ) : (
        <>
          {coaches.length > 0 && (
            <section>
              <h2 className="font-display font-bold text-xl uppercase mb-3 text-slate-300">
                Staff
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {coaches.map((p) => (
                  <PlayerCard key={p.id} p={p} isMe={p.id === user?.id} canManage={canManage} onDeleted={load} />
                ))}
              </div>
            </section>
          )}
          <section>
            <h2 className="font-display font-bold text-xl uppercase mb-3 text-slate-300">
              Joueurs · {regular.length}
            </h2>
            {regular.length === 0 ? (
              <div className="p-6 rounded-xl border border-dashed border-white/10 text-center text-slate-500">
                Pas encore de joueur inscrit
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {regular.map((p) => (
                  <PlayerCard key={p.id} p={p} isMe={p.id === user?.id} canManage={canManage} onDeleted={load} />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function PlayerCard({ p, isMe, canManage, onDeleted }) {
  const isCoach = p.role === "coach" || p.role === "admin";
  const canDelete = canManage && !isMe && p.role !== "admin";

  const del = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm(`Supprimer ${p.name} ?`)) return;
    try {
      await api.delete(`/players/${p.id}`);
      toast.success("Joueur supprimé");
      onDeleted && onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div
      className={`relative p-4 rounded-xl bg-slate-900 border ${
        isMe ? "border-[#E5B85C]/50" : "border-white/10"
      } flex items-center gap-3 hover:border-[#E5B85C]/60 transition-colors group`}
      data-testid={`player-card-${p.id}`}
    >
      <Link to={`/players/${p.id}`} className="flex items-center gap-3 flex-1 min-w-0">
        <Avatar player={p} size={48} />
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm truncate flex items-center gap-2 flex-wrap">
            {p.name}
            {isMe && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#E5B85C]/20 text-[#E5B85C] uppercase tracking-widest font-mono-stat">
                Moi
              </span>
            )}
            {p.role === "admin" && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#E5B85C]/20 text-[#E5B85C] uppercase tracking-widest font-mono-stat">
                DT
              </span>
            )}
            {p.role === "coach" && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300 uppercase tracking-widest font-mono-stat">
                Entraîneur
              </span>
            )}
            {p.is_placeholder && (
              <span
                data-testid={`placeholder-badge-${p.id}`}
                title="Fiche créée par l'entraîneur, en attente d'inscription"
                className="text-[9px] px-1.5 py-0.5 rounded bg-[#E5B85C]/20 text-[#E5B85C] uppercase tracking-widest font-mono-stat"
              >
                Fiche club
              </span>
            )}
          </div>
          <div className="text-xs text-slate-400 truncate">
            {isCoach ? (p.role === "admin" ? "Directeur technique" : "Entraîneur") : p.position || "Joueur"}
          </div>
        </div>
      </Link>
      {canDelete && (
        <button
          data-testid={`delete-player-${p.id}`}
          onClick={del}
          title="Supprimer"
          className="p-2 rounded-lg bg-slate-800 hover:bg-rose-500/20 text-slate-400 hover:text-rose-300 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}

function ImportCSVButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setReport(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API}/players/bulk-import`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Import échoué");
      setReport(data);
      toast.success(
        `${data.counts.created} créé(s), ${data.counts.skipped} ignoré(s)`,
      );
      onDone && onDone();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="flex flex-wrap gap-2 items-center">
      <label
        data-testid="import-csv-button"
        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer text-sm font-bold uppercase tracking-wide ${
          busy ? "bg-slate-700 text-slate-400" : "bg-slate-800 hover:bg-slate-700 text-white"
        }`}
      >
        <Upload className="w-4 h-4" />
        <input
          data-testid="import-csv-file"
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={handle}
          disabled={busy}
        />
        {busy ? "Import..." : "Importer CSV"}
      </label>
      <a
        data-testid="import-csv-template"
        href={`${API}/players/import-template.csv`}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 border border-white/10 hover:border-[#E5B85C]/40 text-slate-300 text-xs font-mono-stat uppercase tracking-widest"
      >
        <FileSpreadsheet className="w-3.5 h-3.5" /> Modèle
      </a>
      {report && (
        <div
          data-testid="import-csv-report"
          className="mt-3 w-full p-4 rounded-xl bg-slate-900 border border-white/10 text-sm space-y-1"
        >
          <div className="text-emerald-400">
            ✓ {report.counts.created} joueur(s) créé(s)
          </div>
          {report.counts.skipped > 0 && (
            <div className="text-yellow-400">
              ⚠ {report.counts.skipped} ligne(s) ignorée(s)
              {report.skipped.slice(0, 5).map((r, i) => (
                <div key={i} className="text-xs text-slate-400 ml-4">
                  Ligne {r.line} · {r.reason}{r.name ? ` (${r.name})` : ""}
                </div>
              ))}
            </div>
          )}
          {report.counts.errors > 0 && (
            <div className="text-rose-400">
              ✗ {report.counts.errors} erreur(s)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

